import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { DASHBOARD, WORKALL } from "../../services/actions";
import {
  Typography,
  Container,
  Grid,
  Box,
  Pagination
} from "@mui/material";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { FaChartLine, FaThermometerHalf, FaChartPie, FaRegLightbulb } from "react-icons/fa";
import PressureMeter from './PressureMeter';
import WorkStatusOverview from './StatusDot';
import './dashboard.css';

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

const colors = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#9333ea", "#ec4899"];

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'center',
      labels: {
        color: "#1e293b",
        font: { size: 12.5, weight: 600, family: 'Poppins, Inter, sans-serif' },
        usePointStyle: true,
        pointStyle: 'circle',
        padding: 20,
        boxWidth: 8,
        boxHeight: 8
      }
    },
    tooltip: {
      padding: 12,
      backgroundColor: "rgba(15, 23, 42, 0.9)",
      titleFont: { size: 13, weight: 'bold', family: 'Inter' },
      bodyFont: { size: 12, family: 'Inter' },
      cornerRadius: 8,
      boxPadding: 6,
      callbacks: {
        afterLabel: function (context) {
          const dataset = context.dataset;
          let extraInfo = [];
          if (dataset.deadline) {
            extraInfo.push(`Deadline: ${new Date(dataset.deadline).toLocaleDateString()}`);
          }
          if (dataset.isOverdue) {
            extraInfo.push(`⚠️ OVERDUE TASK`);
          }
          return extraInfo.length ? extraInfo : null;
        }
      }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#94a3b8", font: { size: 11, family: 'Inter' } },
      border: { display: false },
    },
    y: {
      min: 0,
      max: 100,
      grid: { color: "rgba(226, 232, 240, 0.6)", drawBorder: false },
      ticks: { color: "#94a3b8", font: { size: 11, family: 'Inter' } },
      border: { display: false },
    },
  },
};

const formatdate = (date) => date.split("T")[0];

function findStartEndDate(startDateISO, durationDays) {
  const startDate = new Date(startDateISO);
  startDate.setUTCDate(startDate.getUTCDate() + Number(durationDays));
  let endDateISO = startDate.toISOString();
  return {
    startDate: startDateISO,
    endDate: endDateISO,
    formattedStartDate: formatdate(startDateISO),
    formattedEndDate: formatdate(endDateISO),
  };
}

function splitIntoDays(startDateStr, endDateStr) {
  const parseDate = (str) => {
    const [year, month, day] = str.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const startDate = parseDate(startDateStr);
  const endDate = parseDate(endDateStr);

  const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) || 30;
  const step = totalDays / 4;

  const equalizedDates = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + Math.round(step * i));
    return formatDate(d);
  });

  return {
    totalDays,
    equalizedDates
  };
}

export default function Analytics() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dashboardState = useSelector((state) => state?.dashboardState);
  const workallState = useSelector((state) => state?.workallState);
  const auth = useSelector((state) => state?.loginState?.auth);

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });

  // Pagination State
  const [workPage, setWorkPage] = useState(1);
  const [empPage, setEmpPage] = useState(1);
  const rowsPerPage = 20;

  const getCurrentFinancialYear = () => {
    return new Date().getFullYear().toString();
  };

  const getFinancialYears = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    for (let y = 2022; y <= currentYear + 1; y++) {
      years.push(y.toString());
    }
    return years;
  };

  // Filter State
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [month, setMonth] = useState("");
  const [employeeName, setEmployeeName] = useState("");

  const fetchStatus = () => {
    let url = `dashboard/0/?page=${workPage}`;
    if (financialYear) url += `&financial_year=${financialYear}`;
    if (month) url += `&month=${month}`;
    if (employeeName) url += `&employee_name=${employeeName}`;
    dispatch({ type: `${DASHBOARD}_GET`, payload: url });
  };
  const fetchWorks = () => {
    let url = `work/0/`;
    let filterObj = {};
    
    if (financialYear) filterObj.financial_year = financialYear;
    if (month) filterObj.created_at__month = month;
    
    if (Object.keys(filterObj).length > 0) {
      url += `?filter_by=${encodeURIComponent(JSON.stringify(filterObj))}`;
    }
    
    dispatch({ type: `${WORKALL}_GET`, payload: url });
  };

  useEffect(() => {
    try {
      fetchStatus();
      fetchWorks();
    } catch (err) { }
  }, [workPage, financialYear, month, employeeName]);

  const projectsList = workallState?.data?.results || [];

  // Handle building chart datasets dynamically when selected work changes
  useEffect(() => {
    if (projectsList.length > 0) {
      const selectedId = selectedProjectId || projectsList[0]?.id;
      const prj = projectsList.find(p => p.id == selectedId);
      if (prj) {
        let days = findStartEndDate(prj.start_project_date || new Date().toISOString(), prj.duration || 30);
        let labelsInfo = splitIntoDays(days.formattedStartDate, days.formattedEndDate);
        let labels = labelsInfo.equalizedDates;

        const fetchChartTasks = async () => {
          try {
            const res = await axios.get(`${process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/'}work/task/0/?filter_by={"work_id":${selectedId}}&limit=10`, {
              headers: { Authorization: `Bearer ${auth?.token}` }
            });
            const taskData = res.data?.data?.results || res.data?.results || [];
            let datasets = [];

            if (taskData && taskData.length > 0) {
              const totalTasks = taskData.length;
              const completedTasks = taskData.filter(t => t.status === 'completed').length;
              const workProgress = totalTasks === 0 ? 0 : Math.round((completedTasks / totalTasks) * 100);

              const taskPalette = [
                { border: "#f59e0b", bg: "rgba(245, 158, 11, 0.08)" }, // Amber
                { border: "#10b981", bg: "rgba(16, 185, 129, 0.08)" }, // Emerald Green
                { border: "#8b5cf6", bg: "rgba(139, 92, 246, 0.08)" }, // Purple / Violet
                { border: "#ec4899", bg: "rgba(236, 72, 153, 0.08)" }, // Pink / Rose
                { border: "#06b6d4", bg: "rgba(6, 182, 212, 0.08)" },  // Cyan / Teal
                { border: "#f97316", bg: "rgba(249, 115, 22, 0.08)" }, // Orange
                { border: "#3b82f6", bg: "rgba(59, 130, 246, 0.08)" }, // Blue
                { border: "#84cc16", bg: "rgba(132, 204, 22, 0.08)" }   // Lime
              ];

              datasets = taskData.slice(0, 4).map((t, index) => {
                let val = 0;
                const paletteItem = t.is_overdue 
                  ? { border: "#ef4444", bg: "rgba(239, 68, 68, 0.1)" } 
                  : taskPalette[index % taskPalette.length];

                if (t.status === 'completed') val = 100;
                else if (t.status === 'in_progress') {
                  const check = Array.isArray(t.checklist) ? t.checklist.length : 0;
                  const checked = Array.isArray(t.checkedlist) ? t.checkedlist.length : 0;
                  val = check > 0 ? Math.round((checked / check) * 100) : 50;
                } else if (t.status === 'pending') {
                  val = 15; // show slight baseline so it's not flat
                } else {
                  val = 30;
                }

                let taskVals = Array.from({ length: 5 }, (_, idx) => {
                  return Math.min(Math.round((val / 4) * idx), 100);
                });

                return {
                  label: t.task_name || t.task?.task_name || `Task ${index + 1}`,
                  data: taskVals,
                  borderColor: paletteItem.border,
                  backgroundColor: paletteItem.bg,
                  borderWidth: 2.5,
                  pointRadius: 4,
                  pointHoverRadius: 6,
                  pointBackgroundColor: paletteItem.border,
                  pointBorderColor: "#ffffff",
                  pointBorderWidth: 2,
                  tension: 0.4,
                  fill: true,
                  deadline: t.due_date,
                  isOverdue: t.is_overdue
                };
              });

              // Add Work Overall Status line
              datasets.unshift({
                label: `Work Overall (${prj.status || 'Active'})`,
                data: Array.from({ length: 5 }, (_, idx) => {
                  let overallVal = prj.status === 'completed' ? 100 : (prj.status === 'in_progress' ? Math.max(workProgress, 35) : (prj.status === 'approved' ? 25 : 15));
                  return Math.min(Math.round((overallVal / 4) * idx), 100);
                }),
                borderColor: "#0ea5e9",
                backgroundColor: "rgba(14, 165, 233, 0.1)",
                borderWidth: 3.5,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointBackgroundColor: "#0ea5e9",
                pointBorderColor: "#ffffff",
                pointBorderWidth: 2,
                tension: 0.4,
                fill: true,
                deadline: prj.expected_completion_date,
                isOverdue: prj.is_overdue || false
              });
            } else {
              datasets = [
                {
                  label: "Estimated Progress",
                  data: [10, 35, 60, 80, 100],
                  borderColor: "#6366f1",
                  backgroundColor: "rgba(99, 102, 241, 0.03)",
                  borderWidth: 3,
                  pointRadius: 2,
                  tension: 0.4,
                  fill: true,
                },
                {
                  label: "Actual Progress",
                  data: [10, 30, 50, 75, prj.status === "completed" ? 100 : 85],
                  borderColor: "#10b981",
                  backgroundColor: "rgba(16, 185, 129, 0.03)",
                  borderWidth: 3,
                  pointRadius: 4,
                  pointBackgroundColor: "#10b981",
                  tension: 0.4,
                  fill: true,
                }
              ];
            }

            setChartData({
              labels: labels,
              datasets: datasets
            });
          } catch (e) {
            console.error("Failed to fetch chart tasks:", e);
          }
        };

        fetchChartTasks();
      }
    }
  }, [projectsList, selectedProjectId]);

  // Extract task status progress for the donut chart
  let progress = dashboardState?.data?.counts?.filter((data) => ["Pending", "In Progress", "Completed", "Overdue"].includes(data.name));
  const progressMap = (progress || [])?.reduce((acc, item) => {
    acc[item.name] = item.count;
    return acc;
  }, {});

  const handleProjectChange = (e) => {
    setSelectedProjectId(e.target.value);
  };

  // ── Performance Summary Index — computed from live dashboard data ──────
  const countMap = (dashboardState?.data?.counts || []).reduce((acc, item) => {
    acc[item.name] = item.count || 0;
    return acc;
  }, {});

  const completed = countMap['Completed'] || 0;
  const inProgress = countMap['In Progress'] || 0;
  const pending = countMap['Pending'] || 0;
  const overdue = countMap['Overdue'] || 0;
  const totalTasks = completed + inProgress + pending + overdue;
  const pressure = dashboardState?.data?.presure_score || 0;

  // 1. Avg Completion Adherence
  const adherencePct = totalTasks > 0 ? (completed / totalTasks) * 100 : 0;
  const adherenceValue = totalTasks > 0 ? `${adherencePct.toFixed(1)}%` : '—';
  const adherenceStatus = adherencePct >= 85 ? 'Optimum' : adherencePct >= 60 ? 'On Track' : totalTasks > 0 ? 'Needs Review' : 'No Data';
  const adherenceColor = adherencePct >= 85 ? '#10b981' : adherencePct >= 60 ? '#f59e0b' : '#ef4444';

  // 2. Team Capacity Loading (uses pressure score)
  const capacityValue = `${Number(pressure).toFixed(1)}%`;
  const capacityStatus = pressure < 40 ? 'Under-loaded' : pressure < 70 ? 'Balanced' : pressure < 90 ? 'High Load' : 'Overloaded';
  const capacityColor = pressure < 40 ? '#3b82f6' : pressure < 70 ? '#10b981' : pressure < 90 ? '#f59e0b' : '#ef4444';

  // 3. Task Burn-down (completed vs total)
  const burnValue = `${completed} / ${totalTasks}`;
  const burnRatio = totalTasks > 0 ? completed / totalTasks : 0;
  const burnStatus = burnRatio >= 0.7 ? 'Accelerating' : burnRatio >= 0.4 ? 'On Track' : totalTasks > 0 ? 'Slow' : 'No Data';
  const burnColor = burnRatio >= 0.7 ? '#8b5cf6' : burnRatio >= 0.4 ? '#3b82f6' : '#f59e0b';

  // 4. Work Risk Indicator (ratio based on overdue & pending)
  let riskValue = 'No Risk';
  let riskStatus = 'Stable';
  let riskColor = '#10b981';

  if (totalTasks > 0) {
    const overdueRatio = overdue / totalTasks;
    const pendingRatio = pending / totalTasks;

    if (overdueRatio >= 0.5 || pendingRatio === 1) {
      riskValue = 'High Risk';
      riskStatus = overdueRatio >= 0.5 ? 'Critical' : 'Stalled';
      riskColor = '#ef4444';
    } else if (overdueRatio >= 0.2 || pendingRatio >= 0.7) {
      riskValue = 'Moderate';
      riskStatus = 'Alert';
      riskColor = '#f59e0b';
    } else if (overdue > 0 || pendingRatio >= 0.4) {
      riskValue = 'Low Risk';
      riskStatus = 'Monitor';
      riskColor = '#3b82f6';
    }
  }

  const performanceMetrics = [
    { title: 'Avg Completion Adherence', value: adherenceValue, status: adherenceStatus, color: adherenceColor },
    { title: 'Team Capacity Loading', value: capacityValue, status: capacityStatus, color: capacityColor },
    { title: 'Task Burn-down Rate', value: burnValue, status: burnStatus, color: burnColor },
    { title: 'Work Risk Indicator', value: riskValue, status: riskStatus, color: riskColor },
  ];

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3.5 }, minHeight: '100vh', width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: '#f8fafc' }}>
      {/* Top Banner & Global Filters */}
      <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Analysis Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
            Analyze work progress trends, workload pressure scores, and operational performance metrics.
          </Typography>
        </Box>

        {/* Global Filters */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <select
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            style={{
              padding: '10px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', fontWeight: 600, color: '#475569', outline: 'none', cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Years</option>
            {getFinancialYears().map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{
              padding: '10px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', fontWeight: 600, color: '#475569', outline: 'none', cursor: 'pointer',
              minWidth: '130px'
            }}
          >
            <option value="">All Months</option>
            <option value="1">January</option>
            <option value="2">February</option>
            <option value="3">March</option>
            <option value="4">April</option>
            <option value="5">May</option>
            <option value="6">June</option>
            <option value="7">July</option>
            <option value="8">August</option>
            <option value="9">September</option>
            <option value="10">October</option>
            <option value="11">November</option>
            <option value="12">December</option>
          </select>

          <input
            type="text"
            placeholder="Search Employee..."
            value={employeeName}
            onChange={(e) => setEmployeeName(e.target.value)}
            style={{
              padding: '10px 16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: '#ffffff',
              fontSize: '14px', fontWeight: 600, color: '#475569', outline: 'none',
              minWidth: '200px'
            }}
          />
        </Box>
      </Box>

      {/* Top Row: Chart & Pressure Meter */}
      {false && (
      <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
        {/* Project Progress Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box className="dashboard-card card-interactive" sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ background: '#e0e7ff', p: 1, borderRadius: '10px', display: 'flex', color: '#6366f1' }}>
                  <FaChartLine size={18} />
                </Box>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                  Work Completion & Progress
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 650, color: '#64748b', display: { xs: 'none', sm: 'block' } }}>Work:</Typography>
                <select
                  onChange={handleProjectChange}
                  value={selectedProjectId}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '12px',
                    border: '1px solid #cbd5e1',
                    background: '#f8fafc',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    color: '#0f172a',
                    outline: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                    fontFamily: 'Poppins, sans-serif'
                  }}
                >
                  {projectsList.length === 0 ? (
                    <option value="">No Active Works</option>
                  ) : (
                    projectsList.map((project, index) => (
                      <option key={index} value={project.id}>
                        {project.work_name || `Work ${index + 1}`}
                      </option>
                    ))
                  )}
                </select>
              </Box>
            </Box>

            <Box sx={{ height: '350px', position: "relative", width: '100%' }}>
              <Line data={chartData} options={options} />
            </Box>
          </Box>
        </Grid>

        {/* Load Meter / Pressure Score */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Box className="dashboard-card card-interactive" sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', mb: 4 }}>
              <Box sx={{ background: '#fee2e2', p: 1, borderRadius: '10px', display: 'flex', color: '#ef4444' }}>
                <FaThermometerHalf size={18} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                Workload Pressure Score
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, flexGrow: 1 }}>
              <PressureMeter stroke={16} value={dashboardState?.data?.presure_score} size={160} />
              <Box sx={{ textAlign: 'center', maxWidth: '280px' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Calculation Model
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5, fontWeight: 500 }}>
                  Computed dynamically based on active work weight, status severity, and proximity to due dates.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      )}

      {/* Second Row: Status Overview Donut & Advanced Stats */}
      <Grid container spacing={3} sx={{ width: '100%', mb: 3.5 }}>
        {/* Work Status Overview Donut */}
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Box className="dashboard-card card-interactive" sx={{ p: 3.5, height: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <Box sx={{ background: '#dcfce7', p: 1, borderRadius: '10px', display: 'flex', color: '#10b981' }}>
                <FaChartPie size={18} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                Work Distribution Overview
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '220px', width: '100%' }}>
              <WorkStatusOverview data={progressMap} onStatusClick={(status) => navigate('/works/', { state: { status: status.toLowerCase().replace(' ', '_') } })} />
            </Box>
          </Box>
        </Grid>

        {/* Operational Performance Parameters */}
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Box className="dashboard-card card-interactive" sx={{ p: 3.5, height: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
              <Box sx={{ background: '#fef3c7', p: 1, borderRadius: '10px', display: 'flex', color: '#f59e0b' }}>
                <FaRegLightbulb size={18} />
              </Box>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                Performance Summary Index
              </Typography>
            </Box>

            <Grid container spacing={2.5}>
              {performanceMetrics.map((item, idx) => (
                <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                  <Box sx={{
                    p: 2,
                    borderRadius: '14px',
                    background: '#f8fafc',
                    border: '1px solid #f1f5f9',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    transition: 'transform 0.2s ease',
                    '&:hover': { transform: 'scale(1.02)' }
                  }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b' }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                      {item.value}
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: item.color }} />
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                        {item.status}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      {/* Third Row: Analytics Tables */}
      <Grid container spacing={3} sx={{ width: '100%' }}>
        {/* Active Works Table */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box className="dashboard-card" sx={{ p: 3, height: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Active Works Analysis</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Overview of active works, clients, and allocated workforce.</Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Work Name</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Client</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Status</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Workforce</th>
                  </tr>
                </thead>
                <tbody>
                  {projectsList.slice((workPage - 1) * rowsPerPage, workPage * rowsPerPage).map((prj, idx) => {
                    const daysLeft = prj.expected_completion_date ? Math.ceil((new Date(prj.expected_completion_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                    const isNearDeadline = daysLeft !== null && daysLeft <= 3;
                    const needsHelp = (prj.is_overdue || isNearDeadline) && prj.status !== 'completed';

                    let suggestedEmployee = null;
                    if (needsHelp && dashboardState?.data?.employee_stats?.length > 0) {
                      const assignedNames = prj.assigned_employees || [];
                      const candidates = dashboardState.data.employee_stats.filter((emp) => {
                        if (!emp.employee_name || assignedNames.includes(emp.employee_name)) return false;
                        const activeTasks = (emp.pending || 0) + (emp.in_progress || 0);
                        const activeWorks = emp.total_works || 0;
                        return activeTasks < 2 && activeWorks < 2;
                      });
                      if (candidates.length > 0) {
                        const sorted = [...candidates].sort((a, b) => {
                          const workDiff = (a.total_works || 0) - (b.total_works || 0);
                          if (workDiff !== 0) return workDiff;
                          return (a.pending + a.in_progress) - (b.pending + b.in_progress);
                        });
                        suggestedEmployee = sorted[0].employee_name;
                      }
                    }

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                          {prj.work_name}
                          {needsHelp && suggestedEmployee && (
                            <div style={{ fontSize: 11, color: '#d97706', marginTop: 4, fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FaRegLightbulb size={12} color="#f59e0b" /> Suggestion: Split and Assign to {suggestedEmployee}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 8px', fontSize: 12, color: '#64748b' }}>{prj.client_name}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{
                            padding: '4px 8px', borderRadius: '4px', fontSize: 11, fontWeight: 700,
                            backgroundColor: prj.status === 'completed' ? '#dcfce7' : prj.is_overdue ? '#fee2e2' : prj.status === 'in_progress' ? '#dbeafe' : '#fef3c7',
                            color: prj.status === 'completed' ? '#166534' : prj.is_overdue ? '#ef4444' : prj.status === 'in_progress' ? '#1e40af' : '#b45309'
                          }}>
                            {prj.is_overdue && prj.status !== 'completed' ? 'Overdue' : prj.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 600, color: '#3b82f6' }}>{prj.employee_count || 0} Emp.</td>
                      </tr>
                    );
                  })}
                  {projectsList.length === 0 && (
                    <tr>
                      <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No active works found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {projectsList.length > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(projectsList.length / rowsPerPage)}
                    page={workPage}
                    onChange={(e, val) => setWorkPage(val)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Work Performance Based on Employee Table */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box className="dashboard-card" sx={{ p: 3, height: '100%', boxSizing: 'border-box' }}>
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>Work Performance Based on Employee</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>Detailed breakdown of assigned works by employee and overall completion tracking.</Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Employee</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Assigned Works</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Completed</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>In-progress</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Pending</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Review</th>
                    <th style={{ padding: '12px 8px', fontSize: 12, color: '#475569', fontWeight: 700 }}>Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {(dashboardState?.data?.employee_stats || []).slice((empPage - 1) * rowsPerPage, empPage * rowsPerPage).map((emp, idx) => {
                    const comp = emp.completed || 0;

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{emp.employee_name}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#6366f1' }}>{emp.total_works || 0}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#10b981' }}>{comp}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#3b82f6' }}>{emp.in_progress || 0}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#f59e0b' }}>{emp.pending || 0}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>{emp.review || 0}</td>
                        <td style={{ padding: '12px 8px', fontSize: 13, fontWeight: 700, color: '#16a34a' }}>{emp.approved || 0}</td>
                      </tr>
                    );
                  })}
                  {(!dashboardState?.data?.employee_stats || dashboardState?.data?.employee_stats.length === 0) && (
                    <tr>
                      <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No employee data available.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {(dashboardState?.data?.employee_stats?.length || 0) > rowsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(dashboardState.data.employee_stats.length / rowsPerPage)}
                    page={empPage}
                    onChange={(e, val) => setEmpPage(val)}
                    color="primary"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
